import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { toast } from "react-toastify";

export default function AdminStaffAttendanceSalary() {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(location.state || null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch staff info if not available
      let currentStaffInfo = staffInfo;
      if (!currentStaffInfo) {
        const staffResponse = await fetch(`${API_BASE_URL}/api/staff`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (staffResponse.ok) {
          const allStaff = await staffResponse.json();
          const staff = allStaff.find((s) => String(s.StaffId) === String(staffId) || String(s.staffId) === String(staffId));
          if (staff) {
            currentStaffInfo = staff;
            setStaffInfo(staff);
          }
        }
      }

      // Fetch ALL attendance records for this staff member
      const attendanceResponse = await fetch(`${API_BASE_URL}/api/staff/attendance/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (attendanceResponse.ok) {
        const attendanceData = await attendanceResponse.json();
        setAttendanceRecords(attendanceData || []);
      } else {
        console.error("Failed to fetch attendance:", await attendanceResponse.text());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Generate all months from joining date to current month
  const generateAllMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    // If no staff info, only show current month
    if (!staffInfo || !staffInfo.JoiningDate) {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const monthYear = currentDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      const monthRecords = attendanceRecords.filter((record) => {
        const recordDate = new Date(record.Date);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });

      return [
        {
          monthYear,
          month,
          year,
          recordCount: monthRecords.length,
        },
      ];
    }
    
    // Start from joining month
    const startDate = new Date(staffInfo.JoiningDate);
    let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    while (iterDate <= endDate) {
      const month = iterDate.getMonth() + 1; // 1-12
      const year = iterDate.getFullYear();
      const monthYear = iterDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      // Count records for this specific month
      const monthRecords = attendanceRecords.filter((record) => {
        const recordDate = new Date(record.Date);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
      
      months.push({
        monthYear,
        month,
        year,
        recordCount: monthRecords.length,
      });
      
      // Move to next month
      iterDate.setMonth(iterDate.getMonth() + 1);
    }
    
    // Return in reverse order (most recent first)
    return months.reverse();
  };

  const handleViewAttendance = (month, year) => {
    navigate(`/admin/staff/${staffId}/attendance-calendar/${month}/${year}`, {
      state: staffInfo,
    });
  };

  const handleViewSalary = (month, year) => {
    navigate(`/admin/staff/${staffId}/salary-details/${month}/${year}`, {
      state: staffInfo,
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            background: "linear-gradient(180deg,#0b0f15 0%, #121316 100%)",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box 
        sx={{ 
          display: "flex",
          justifyContent: "center",
          minHeight: "calc(100vh - 80px)",
          p: { xs: 1, sm: 2, md: 4 }
        }}
      >
        <Box 
          sx={{ 
            width: "100%",
            maxWidth: "1200px", // Maximum width for large screens
            mx: "auto" // Center horizontally
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: "flex", 
            alignItems: { xs: "flex-start", sm: "center" }, 
            flexDirection: { xs: "column", sm: "row" },
            mb: 3,
            gap: { xs: 2, sm: 0 }
          }}>
            <IconButton
              onClick={() => navigate("/staff")}
              sx={{
                mr: { xs: 0, sm: 2 },
                bgcolor: "#8B7355",
                color: "white",
                "&:hover": { bgcolor: "#6D5A43" },
                alignSelf: { xs: "flex-start", sm: "auto" }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  color: "#fff",
                  textDecoration: "underline",
                  textDecorationColor: "#8B7355",
                  textDecorationThickness: 2,
                  textUnderlineOffset: 8,
                  fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
                  lineHeight: { xs: 1.2, sm: 1.4 }
                }}
              >
                {staffInfo?.StaffType === "Hourly" ? "ATTENDANCE & SALARY DETAILS" : "SALARY DETAILS"}
              </Typography>
              {staffInfo && (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: "#C4A574", 
                    mt: 1,
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {staffInfo.FullName} - {staffInfo.Designation}
                </Typography>
              )}
            </Box>
          </Box>

        {/* Salary and Attendance Records - Monthly View */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2d2d2d" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: "#C4A574",
                fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" }
              }}
            >
              {staffInfo?.StaffType === "Task-Based" ? "Salary Records" : "Salary and Attendance Records"}
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.04)" }} />

            <Box>
              {generateAllMonths().length === 0 ? (
                <Typography align="center" sx={{ py: 4, color: "#9CA3AF" }}>
                  No months to display
                </Typography>
              ) : (
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {generateAllMonths().map((data) => (
                    <Grid item xs={12} key={data.monthYear}>
                      <Card
                        sx={{
                          mb: { xs: 1, sm: 2 },
                          bgcolor: "#2d2d2d",
                          boxShadow: 1,
                          border: "1px solid rgba(255,255,255,0.04)",
                          "&:hover": { boxShadow: 3 },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", sm: "center" },
                              flexDirection: { xs: "column", sm: "row" },
                              gap: { xs: 2, sm: 2 },
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: "#fff",
                                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                                  wordBreak: "break-word"
                                }}
                              >
                                {data.monthYear}
                              </Typography>
                              {staffInfo?.StaffType !== "Task-Based" && (
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: "#9CA3AF",
                                    fontSize: { xs: "0.8rem", sm: "0.875rem" }
                                  }}
                                >
                                  {data.recordCount} day{data.recordCount !== 1 ? "s" : ""} recorded
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ 
                              display: "flex", 
                              gap: { xs: 1, sm: 2 },
                              flexDirection: { xs: "column", sm: "row" },
                              width: { xs: "100%", sm: "auto" },
                              flexShrink: 0
                            }}>
                              {staffInfo?.StaffType !== "Task-Based" && (
                                <Button
                                  variant="contained"
                                  startIcon={<VisibilityIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />}
                                  onClick={() => handleViewAttendance(data.month, data.year)}
                                  sx={{
                                    bgcolor: "#8B7355",
                                    color: "white",
                                    "&:hover": { bgcolor: "#6D5A43" },
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    px: { xs: 2, sm: 3 },
                                    py: { xs: 1, sm: 1.5 },
                                    minWidth: { xs: "auto", sm: "140px" },
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                                    View Attendance
                                  </Box>
                                  <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                                    Attendance
                                  </Box>
                                </Button>
                              )}
                              <Button
                                variant="contained"
                                startIcon={<AttachMoneyIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />}
                                onClick={() => handleViewSalary(data.month, data.year)}
                                sx={{
                                  bgcolor: "#0288D1",
                                  color: "white",
                                  "&:hover": { bgcolor: "#0277BD" },
                                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                  px: { xs: 2, sm: 3 },
                                  py: { xs: 1, sm: 1.5 },
                                  minWidth: { xs: "auto", sm: "120px" },
                                  whiteSpace: "nowrap"
                                }}
                              >
                                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                                  View Salary
                                </Box>
                                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                                  Salary
                                </Box>
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </CardContent>
        </Card>
        </Box>
      </Box>
    </AppLayout>
  );
}
